import { useState, type ComponentProps, type ReactNode } from 'react'
import { toast } from 'sonner'

import type { components } from '@/api/schema'
import { ActionPopover } from '@/components/action-popover'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { StatusPill } from '@/components/status-pill'
import { Input } from '@/components/ui/input'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import { type ItemStatusChoice, useSetItemStatuses } from './hooks'

type LineStatus = components['schemas']['LineStatusRead']

const CHOICES: { value: ItemStatusChoice; label: string }[] = [
  { value: 'backordered', label: 'Backordered' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'clear', label: 'Automatic' },
]

/** An item's status pill; it is the trigger of that item's status popover. */
export function ItemStatusButton({
  status,
  model,
  ...props
}: {
  status: LineStatus | undefined
  model: string
} & ComponentProps<'button'>) {
  if (!status) return null
  return (
    <button
      type="button"
      {...props}
      className="shrink-0 whitespace-nowrap rounded-full focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      aria-label={`${model}: ${status.label}. Change status`}
      title={status.note ?? (status.set_by ? `Set by ${status.set_by}` : 'Change status')}
    >
      <StatusPill tone={status.tone} className="cursor-pointer hover:brightness-95">
        {status.label}
      </StatusPill>
    </button>
  )
}

/** Set one status on one or more PO items, from a popover on its trigger. Cancelled items come off the PO total. */
export function ItemStatusPopover({
  poUuid,
  items,
  poTotal,
  cancelAmount,
  trigger,
  open,
  onOpenChange,
  onSaved,
}: {
  poUuid: string
  items: { id: number; model: string }[]
  poTotal: string
  /** What the PO total drops by if these items are cancelled (sub-lines included). */
  cancelAmount: number
  trigger: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}) {
  const [choice, setChoice] = useState<ItemStatusChoice | null>(null)
  const [untilDate, setUntilDate] = useState('')
  const mutation = useSetItemStatuses(poUuid)
  const withNote = choice !== null && choice !== 'clear'

  const setOpen = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      setChoice(null)
      setUntilDate('')
    }
  }

  const save = (note: string, close: () => void) => {
    if (!choice) return
    mutation.mutate(
      {
        line_item_ids: items.map((item) => item.id),
        status: choice,
        until_date: choice === 'backordered' && untilDate ? untilDate : null,
        note: (withNote && note) || null,
      },
      {
        onSuccess: () => {
          toast.success(
            items.length === 1
              ? `${items[0]?.model} set to ${CHOICES.find((c) => c.value === choice)?.label}`
              : `${items.length} items set to ${CHOICES.find((c) => c.value === choice)?.label}`,
          )
          close()
          onSaved?.()
        },
        onError: (error) => toast.error(formatApiErrorMessage('Item status not saved', error)),
      },
    )
  }

  return (
    <ActionPopover
      trigger={trigger}
      open={open}
      onOpenChange={setOpen}
      title={items.length === 1 ? items[0]?.model : `${items.length} items`}
      note={withNote ? { label: 'Note', placeholder: 'Note (optional)' } : undefined}
      confirmLabel={
        choice === 'cancelled' ? (items.length === 1 ? 'Cancel item' : 'Cancel items') : 'Save'
      }
      confirmVariant={choice === 'cancelled' ? 'destructive' : 'default'}
      canConfirm={choice !== null}
      pending={mutation.isPending}
      pendingLabel="Saving..."
      onConfirm={save}
    >
      <ReviewChoiceGroup
        label="Item status"
        value={choice}
        options={CHOICES}
        onChange={setChoice}
        disabled={mutation.isPending}
      />
      {choice === 'backordered' && (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Until</span>
          <Input
            type="date"
            value={untilDate}
            onChange={(event) => setUntilDate(event.target.value)}
            className="h-8 w-40 text-sm"
          />
        </label>
      )}
      {choice === 'cancelled' && (
        <p className="text-sm tabular-nums">
          PO total {formatMoney(poTotal)} → {formatMoney(Number(poTotal) - cancelAmount)}
        </p>
      )}
    </ActionPopover>
  )
}
